import { METRIC_GROUPS } from './extendedMetrics';
import { availableMonths, meanScore } from './filters';
import { METRIC_BY_STAGE } from './funnel';
import type { YougovMonthlyRow } from './yougovData.schema';

/**
 * Selector options, funnel first. Purchase Intent leads because it is the
 * bottom-of-funnel number the page opens on. `index` is omitted: it is not part
 * of the tracked vocabulary and its semantics are undocumented.
 */
export const TREND_METRICS: string[] = [
  METRIC_BY_STAGE.purchaseIntent,
  METRIC_BY_STAGE.awareness,
  METRIC_BY_STAGE.consideration,
  ...METRIC_GROUPS.awareness,
  ...METRIC_GROUPS.perception,
  ...METRIC_GROUPS.ownership,
];

export type TrendPoint = { month: string } & Record<string, number | null | string>;

export function computeTrendSeries(
  rows: YougovMonthlyRow[],
  metric: string,
  brands: string[],
): { months: string[]; points: TrendPoint[] } {
  const metricRows = rows.filter((r) => r.metric === metric);
  // Span every month in the filtered range, not just the months this metric reports.
  // A month the metric skipped then yields null for every brand, so connectNulls={false}
  // breaks the line at the gap rather than closing it up into a false trend.
  const months = availableMonths(rows);
  const points = months.map((month) => {
    const point: TrendPoint = { month };
    for (const brand of brands) {
      point[brand] = meanScore(metricRows.filter((r) => r.month === month && r.brand === brand));
    }
    return point;
  });
  return { months, points };
}

/** Most recent measured value per brand, ranked descending for the legend. */
export function latestValues(
  points: TrendPoint[],
  brands: string[],
): Array<{ brand: string; value: number | null }> {
  return brands
    .map((brand) => {
      for (let i = points.length - 1; i >= 0; i--) {
        const value = points[i][brand];
        if (typeof value === 'number') return { brand, value };
      }
      return { brand, value: null };
    })
    .sort((a, b) => (b.value ?? Number.NEGATIVE_INFINITY) - (a.value ?? Number.NEGATIVE_INFINITY));
}
